import { useQuery } from '@tanstack/react-query'
import { Printer, ReceiptText } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  EmptyState,
  InlineAlert,
  QueryState,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchSalesByDay, fetchSalesReport } from '../lib/api/reports.ts'
import {
  dayLabel,
  mostBought,
  pieces,
  reais,
  SALES_BY_DAY_EXPORT_COLUMNS,
  SALES_EXPORT_COLUMNS,
  saleTitle,
  salesByDayPrint,
  salesDetailPrint,
  salesExportRows,
  weekdayOf,
  weekLabel,
  weeksOf,
  type SalesReportMode,
} from '../lib/sales-period-report.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

import { ExportMenu } from './export-menu.tsx'
import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * Relatórios → Vendas do período — F271.
 *
 * Eduardo: a cliente faz nota das compras de vários dias, e a atendente
 * precisa imprimir "cada peça, cada venda e o valor total" para tirar a nota
 * no GDOOR. E o consolidado: "tipo semanal, só com valor total".
 *
 * Os filtros (período, filial, cliente, modo) moram na barra da página; aqui
 * fica o que se lê, o que se imprime e o que se exporta — do MESMO dado.
 */
export const SalesPeriodReport = ({
  range,
  branchId,
  branchName,
  customerId,
  mode,
  toolbar,
}: {
  /** Os dias escolhidos: `from` inclusivo, `to` o último dia (inclusivo). */
  range: { from: string; to: string; end: string } | null
  branchId: string
  branchName: string
  customerId: string
  mode: SalesReportMode
  /** A barra de filtros da página, com as ações daqui ao lado. */
  toolbar: (actions: ReactNode) => ReactNode
}): ReactNode => {
  const company = useCompanyHeader()
  const template = useReceiptTemplate()
  const [printing, setPrinting] = useState(false)

  const filter =
    range === null
      ? null
      : {
          from: range.from,
          to: range.end,
          ...(branchId === '' ? {} : { branchId }),
          ...(customerId === '' ? {} : { customerId }),
        }

  const detail = useQuery({
    queryKey: ['report-sales', filter],
    queryFn: async ({ signal }) => {
      if (filter === null) throw new Error('período incompleto')
      return await fetchSalesReport(filter, signal)
    },
    enabled: filter !== null && mode === 'detalhado',
  })
  const byDay = useQuery({
    queryKey: ['report-sales-by-day', filter],
    queryFn: async ({ signal }) => {
      if (filter === null) throw new Error('período incompleto')
      return await fetchSalesByDay(filter, signal)
    },
    enabled: filter !== null && mode === 'consolidado',
  })
  const query = mode === 'detalhado' ? detail : byDay

  // O nome do cliente vem da própria venda: o filtro garante que é um só.
  const customerName =
    customerId === '' ? null : (detail.data?.sales[0]?.customerName ?? null)

  const header =
    range === null
      ? null
      : {
          company,
          template,
          from: range.from,
          to: range.to,
          branchName,
          customerName,
        }

  const printable =
    header === null
      ? null
      : mode === 'detalhado'
        ? detail.data === undefined
          ? null
          : salesDetailPrint(header, detail.data)
        : byDay.data === undefined
          ? null
          : salesByDayPrint(header, byDay.data)

  const detailRows =
    detail.data === undefined ? [] : salesExportRows(detail.data)
  const bought =
    customerId === '' || detail.data === undefined
      ? []
      : mostBought(detail.data.sales)
  const dayRows = byDay.data?.days ?? []

  const totals = mode === 'detalhado' ? detail.data?.totals : byDay.data?.totals

  const actions = (
    <>
      <Button
        variant="secondary"
        disabled={printable === null || totals?.sales === 0}
        leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
        onClick={() => {
          setPrinting(true)
        }}
      >
        Imprimir / PDF
      </Button>
      {mode === 'detalhado' ? (
        <ExportMenu
          fileName="vendas-do-periodo"
          sheetName="Vendas do período"
          columns={SALES_EXPORT_COLUMNS}
          fetchPage={() =>
            Promise.resolve({ items: detailRows, total: detailRows.length })
          }
          pageSize={Math.max(detailRows.length, 1)}
          total={detailRows.length}
        />
      ) : (
        <ExportMenu
          fileName="vendas-por-dia"
          sheetName="Vendas por dia"
          columns={SALES_BY_DAY_EXPORT_COLUMNS}
          fetchPage={() =>
            Promise.resolve({ items: dayRows, total: dayRows.length })
          }
          pageSize={Math.max(dayRows.length, 1)}
          total={dayRows.length}
        />
      )}
    </>
  )

  return (
    <>
      {toolbar(actions)}

      {range === null ? (
        <InlineAlert tone="warning">
          Escolha as duas datas, a primeira antes da segunda.
        </InlineAlert>
      ) : null}

      <div
        role="group"
        aria-label="Números do período"
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatTile
          label="Valor total"
          value={totals === undefined ? '—' : reais(totals.total)}
          loading={query.isPending && filter !== null}
          error={query.isError}
        />
        <StatTile
          label="Vendas"
          value={totals === undefined ? '—' : String(totals.sales)}
          loading={query.isPending && filter !== null}
          error={query.isError}
        />
        <StatTile
          label="Total bruto"
          value={totals === undefined ? '—' : reais(totals.subtotal)}
          loading={query.isPending && filter !== null}
          error={query.isError}
        />
        <StatTile
          label="Descontos"
          value={totals === undefined ? '—' : reais(totals.discount)}
          loading={query.isPending && filter !== null}
          error={query.isError}
        />
      </div>

      {detail.data?.truncated === true && mode === 'detalhado' ? (
        <InlineAlert tone="warning">
          {`A lista mostra as primeiras ${String(detail.data.sales.length)} vendas. Escolha um período menor para ver todas; os números de cima são do período inteiro.`}
        </InlineAlert>
      ) : null}

      <QueryState
        isPending={query.isPending && filter !== null}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="as vendas do período"
        isEmpty={(totals?.sales ?? 0) === 0}
        empty={
          <EmptyState
            icon={<ReceiptText aria-hidden="true" className="h-8 w-8" />}
            title="Nenhuma venda neste período"
            description="Escolha outro período, outro cliente, ou tire o filtro de filial."
          />
        }
      >
        {mode === 'detalhado' ? (
          <div className="flex flex-col gap-3">
            {/*
              F299: de um cliente só, o que ele mais compra, antes das vendas —
              "pra fazer uma base do que posso enfiar mais nele".
            */}
            {bought.length === 0 ? null : (
              <TableContainer>
                <Table aria-label="O que mais compra">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>O que mais compra</TableHeaderCell>
                      <TableHeaderCell numeric>Qtd</TableHeaderCell>
                      <TableHeaderCell numeric>Vendas</TableHeaderCell>
                      <TableHeaderCell numeric>Total</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bought.map((item) => (
                      <TableRow key={item.sku ?? `~${item.description}`}>
                        <TableCell>
                          {item.description}
                          {item.sku === null ? null : (
                            <span className="ml-2 text-(length:--text-xs) text-(--text-muted)">
                              {item.sku}
                            </span>
                          )}
                        </TableCell>
                        <TableCell numeric>{pieces(item.quantity)}</TableCell>
                        <TableCell numeric>{item.sales}</TableCell>
                        <TableCell numeric>{reais(item.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <ol className="flex flex-col gap-3" aria-label="Vendas">
              {(detail.data?.sales ?? []).map((sale) => (
                <li
                  key={sale.id}
                  className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-3"
                >
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-(--weight-medium)">{saleTitle(sale)}</p>
                    <p className="text-(length:--text-sm) text-(--text-secondary)">
                      {sale.customerName}
                      {sale.customerDocument === null
                        ? ''
                        : ` · ${sale.customerDocument}`}
                    </p>
                  </div>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>Produto</TableHeaderCell>
                          <TableHeaderCell numeric>Qtd</TableHeaderCell>
                          <TableHeaderCell numeric>Unitário</TableHeaderCell>
                          <TableHeaderCell numeric>Desconto</TableHeaderCell>
                          <TableHeaderCell numeric>Total</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sale.lines.map((line, index) => (
                          <TableRow key={`${sale.id}-${String(index)}`}>
                            <TableCell>
                              {line.description}
                              {line.sku === null ? null : (
                                <span className="ml-2 text-(length:--text-xs) text-(--text-muted)">
                                  {line.sku}
                                </span>
                              )}
                            </TableCell>
                            <TableCell numeric>
                              {pieces(line.quantity)}
                            </TableCell>
                            <TableCell numeric>
                              {reais(line.unitPrice)}
                            </TableCell>
                            <TableCell numeric>
                              {line.discount === '0.00'
                                ? '—'
                                : reais(line.discount)}
                            </TableCell>
                            <TableCell numeric>{reais(line.total)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-(--weight-medium)">
                          <TableCell>Total da venda</TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell numeric>
                            {sale.discount === '0.00'
                              ? '—'
                              : reais(sale.discount)}
                          </TableCell>
                          <TableCell numeric>{reais(sale.total)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {weeksOf(dayRows).map((week) => (
              <TableContainer key={week.monday}>
                <Table aria-label={weekLabel(week)}>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{weekLabel(week)}</TableHeaderCell>
                      <TableHeaderCell numeric>Vendas</TableHeaderCell>
                      <TableHeaderCell numeric>Descontos</TableHeaderCell>
                      <TableHeaderCell numeric>Total</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {week.days.map((day) => (
                      <TableRow key={day.day}>
                        <TableCell>
                          {`${weekdayOf(day.day)} ${dayLabel(day.day)}`}
                        </TableCell>
                        <TableCell numeric>{day.sales}</TableCell>
                        <TableCell numeric>{reais(day.discount)}</TableCell>
                        <TableCell numeric>{reais(day.total)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-(--weight-medium)">
                      <TableCell>Total da semana</TableCell>
                      <TableCell numeric>{week.sales}</TableCell>
                      <TableCell numeric>{reais(week.discount)}</TableCell>
                      <TableCell numeric>{reais(week.total)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
            {(byDay.data?.payments ?? []).length === 0 ? null : (
              <TableContainer>
                <Table aria-label="Por forma de pagamento">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Por forma de pagamento</TableHeaderCell>
                      <TableHeaderCell numeric>Valor</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(byDay.data?.payments ?? []).map((payment) => (
                      <TableRow key={payment.name}>
                        <TableCell>{payment.name}</TableCell>
                        <TableCell numeric>{reais(payment.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}
      </QueryState>

      {printing && printable !== null ? (
        <PrintDocumentDialog
          document={printable}
          description="Vendas do período"
          initialPaper="a4"
          onClose={() => {
            setPrinting(false)
          }}
        />
      ) : null}
    </>
  )
}
